import React, { useState, useEffect } from 'react';
import { memberAPI, emailAPI } from '../services/api';
import './SendEmailModal.css';

const SendEmailModal = ({ isOpen, onClose, onEmailSent }) => {
  const [step, setStep] = useState(1); // 1=select member, 2=select template, 3=fill variables, 4=preview, 5=sending
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Member selection
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Template selection
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  
  // Variables
  const [variables, setVariables] = useState({});
  
  // Preview
  const [emailPreview, setEmailPreview] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    } else {
      resetModal();
    }
  }, [isOpen]);

  const resetModal = () => {
    setStep(1);
    setSelectedMember(null);
    setSelectedTemplate(null);
    setTemplateDetails(null);
    setVariables({});
    setEmailPreview(null);
    setMemberSearch('');
    setError(null);
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load members and templates in parallel
      const [membersResponse, templatesResponse] = await Promise.all([
        memberAPI.getAll({ limit: 1000, sortBy: 'lastName', sortOrder: 'asc' }),
        emailAPI.getTemplates()
      ]);
      
      const membersList = membersResponse.data.data.members;
      console.log(`SendEmailModal: Loaded ${membersList.length} members`);
      
      // Debug: Check if Derek Lovrenich is in the list
      const derekMember = membersList.find(m => 
        (m.firstName?.toLowerCase().includes('derek') && m.lastName?.toLowerCase().includes('lovrenich')) ||
        m.email?.toLowerCase().includes('derek')
      );
      if (derekMember) {
        console.log('Found Derek Lovrenich:', derekMember);
      } else {
        console.log('Derek Lovrenich not found in member list');
        // Show first few members for debugging
        console.log('First 5 members:', membersList.slice(0, 5).map(m => `${m.firstName} ${m.lastName} (${m.ghin})`));
      }
      
      setMembers(membersList);
      setTemplates(templatesResponse.data.data || templatesResponse.data);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (member) => {
    setSelectedMember(member);
    setStep(2);
  };

  const handleTemplateSelect = async (template) => {
    try {
      setLoading(true);
      setSelectedTemplate(template);
      
      // Get full template details including variables
      const response = await emailAPI.getTemplate(template._id);
      const details = response.data.data || response.data;
      setTemplateDetails(details);
      
      // Initialize variables object
      const initialVars = {};
      details.variables.forEach(variable => {
        initialVars[variable.name] = variable.defaultValue || '';
      });
      setVariables(initialVars);
      
      setStep(3);
    } catch (err) {
      console.error('Error loading template:', err);
      setError('Failed to load template details');
    } finally {
      setLoading(false);
    }
  };

  const handleVariableChange = (variable, value) => {
    setVariables(prev => ({
      ...prev,
      [variable]: value
    }));
  };

  const handlePreview = async () => {
    try {
      setLoading(true);
      
      // Get email preview with variables substituted
      const response = await emailAPI.previewEmail(selectedTemplate._id, {
        ...variables,
        // Add member-specific variables
        memberName: selectedMember.firstName + ' ' + selectedMember.lastName,
        memberEmail: selectedMember.email,
        memberGhin: selectedMember.ghin,
        firstName: selectedMember.firstName,
        lastName: selectedMember.lastName
      });
      
      setEmailPreview(response.data.data || response.data);
      setStep(4);
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Failed to generate preview: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    try {
      setLoading(true);
      setStep(5);
      
      const emailData = {
        memberId: selectedMember._id,
        templateId: selectedTemplate._id,
        variables: {
          ...variables,
          // Add member-specific variables
          memberName: selectedMember.firstName + ' ' + selectedMember.lastName,
          memberEmail: selectedMember.email,
          memberGhin: selectedMember.ghin,
          firstName: selectedMember.firstName,
          lastName: selectedMember.lastName
        }
      };
      
      await emailAPI.sendEmail(emailData);
      
      // Success!
      if (onEmailSent) {
        onEmailSent(selectedMember, selectedTemplate);
      }
      
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (err) {
      console.error('Error sending email:', err);
      setError('Failed to send email: ' + (err.response?.data?.error || err.message));
      setStep(4); // Go back to preview
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(member => {
    const searchTerm = memberSearch.trim();
    
    // Only filter if search term has 2 or more characters
    if (searchTerm.length < 2) return false;
    
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
    const reverseName = `${member.lastName} ${member.firstName}`.toLowerCase();
    
    return (
      member.firstName?.toLowerCase().includes(searchLower) ||
      member.lastName?.toLowerCase().includes(searchLower) ||
      fullName.includes(searchLower) ||
      reverseName.includes(searchLower) ||
      member.email?.toLowerCase().includes(searchLower) ||
      member.ghin?.toString().includes(searchLower) ||
      member.displayName?.toLowerCase().includes(searchLower) ||
      member.nickname?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="send-email-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            📧 Send Email
            {selectedMember && (
              <span className="selected-member-info">
                to {selectedMember.firstName} {selectedMember.lastName}
              </span>
            )}
          </h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => setError(null)} className="btn btn-outline">
                Dismiss
              </button>
            </div>
          )}

          {/* Step 1: Select Member */}
          {step === 1 && (
            <div className="step-content">
              <h3>Step 1: Select Member</h3>
              <p>Choose the member you want to send an email to:</p>
              
              <div className="member-search">
                <input
                  type="text"
                  placeholder="Search members by name, email, or GHIN..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="search-input"
                />
              </div>
              
              <div className="member-list">
                {loading ? (
                  <div className="loading-spinner">Loading members...</div>
                ) : memberSearch.trim().length < 2 ? (
                  <div className="search-hint">Type at least 2 characters to search members...</div>
                ) : filteredMembers.length === 0 ? (
                  <div className="no-results">No members found matching "{memberSearch}"</div>
                ) : (
                  filteredMembers.slice(0, 10).map(member => (
                    <div
                      key={member._id}
                      className="member-option"
                      onClick={() => handleMemberSelect(member)}
                    >
                      <div className="member-info">
                        <div className="member-name">
                          {member.firstName} {member.lastName}
                        </div>
                        <div className="member-details">
                          {member.email} • GHIN: {member.ghin}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 2: Select Template */}
          {step === 2 && (
            <div className="step-content">
              <h3>Step 2: Select Email Template</h3>
              <p>Choose an email template:</p>
              
              <div className="template-list">
                {loading ? (
                  <div className="loading-spinner">Loading templates...</div>
                ) : templates.length === 0 ? (
                  <div className="no-results">No templates available</div>
                ) : (
                  templates.map(template => (
                    <div
                      key={template._id}
                      className="template-option"
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <div className="template-info">
                        <div className="template-name">{template.name}</div>
                        <div className="template-subject">{template.subject}</div>
                        <div className="template-meta">
                          <span className={`category-badge ${template.category}`}>
                            {template.category}
                          </span>
                          <span className="variable-count">
                            {template.variables.length} variables
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="step-actions">
                <button onClick={() => setStep(1)} className="btn btn-outline">
                  ← Back
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Fill Variables */}
          {step === 3 && templateDetails && (
            <div className="step-content">
              <h3>Step 3: Fill Template Variables</h3>
              <p>Provide values for the template variables:</p>
              
              <div className="template-info-box">
                <strong>{selectedTemplate.name}</strong>
                <div className="template-subject">Subject: {selectedTemplate.subject}</div>
              </div>
              
              {templateDetails.variables.length === 0 ? (
                <div className="no-variables">
                  <p>This template doesn't require any custom variables.</p>
                  <p>Member information will be filled automatically.</p>
                </div>
              ) : (
                <div className="variables-form">
                  {templateDetails.variables.map(variable => (
                    <div key={variable.name} className="form-group">
                      <label htmlFor={variable.name}>
                        {variable.name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        {variable.required && <span className="required">*</span>}
                      </label>
                      {variable.description && (
                        <p className="variable-description">{variable.description}</p>
                      )}
                      <input
                        type="text"
                        id={variable.name}
                        value={variables[variable.name] || ''}
                        onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                        placeholder={variable.defaultValue || `Enter ${variable.name}`}
                        className="variable-input"
                        required={variable.required}
                      />
                    </div>
                  ))}
                </div>
              )}
              
              <div className="automatic-variables">
                <h4>Automatic Variables</h4>
                <p>These will be filled automatically from member data:</p>
                <ul>
                  <li><strong>memberName:</strong> {selectedMember.firstName} {selectedMember.lastName}</li>
                  <li><strong>memberEmail:</strong> {selectedMember.email}</li>
                  <li><strong>memberGhin:</strong> {selectedMember.ghin}</li>
                  <li><strong>firstName:</strong> {selectedMember.firstName}</li>
                  <li><strong>lastName:</strong> {selectedMember.lastName}</li>
                </ul>
              </div>
              
              <div className="step-actions">
                <button onClick={() => setStep(2)} className="btn btn-outline">
                  ← Back
                </button>
                <button onClick={handlePreview} className="btn btn-primary" disabled={loading}>
                  {loading ? 'Generating...' : 'Preview Email →'}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Preview */}
          {step === 4 && emailPreview && (
            <div className="step-content">
              <h3>Step 4: Preview & Send</h3>
              <p>Review the email before sending:</p>
              
              <div className="email-preview">
                <div className="preview-header">
                  <div className="preview-field">
                    <strong>To:</strong> {selectedMember.email}
                  </div>
                  <div className="preview-field">
                    <strong>Subject:</strong> {emailPreview.subject}
                  </div>
                </div>
                
                <div className="preview-body">
                  {emailPreview.isHtml ? (
                    <div 
                      className="html-preview"
                      dangerouslySetInnerHTML={{ __html: emailPreview.body }}
                    />
                  ) : (
                    <pre className="text-preview">{emailPreview.body}</pre>
                  )}
                </div>
              </div>
              
              <div className="step-actions">
                <button onClick={() => setStep(3)} className="btn btn-outline">
                  ← Back
                </button>
                <button onClick={handleSendEmail} className="btn btn-primary">
                  📧 Send Email
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Sending */}
          {step === 5 && (
            <div className="step-content sending-step">
              {loading ? (
                <>
                  <div className="loading-spinner large"></div>
                  <h3>Sending Email...</h3>
                  <p>Please wait while the email is being sent.</p>
                </>
              ) : (
                <>
                  <div className="success-icon">✅</div>
                  <h3>Email Sent Successfully!</h3>
                  <p>
                    Email sent to {selectedMember.firstName} {selectedMember.lastName} 
                    at {selectedMember.email}
                  </p>
                  <p>This modal will close automatically.</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;